import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import Layout from '@/components/Layout'
import HomePage from '@/pages/HomePage'
import AssetsPage from '@/pages/AssetsPage'
import EditorPage from '@/pages/EditorPage'
import ProjectsPage from '@/pages/ProjectsPage'
import GeneratePage from '@/pages/GeneratePage'
import AnalyzerPage from '@/pages/AnalyzerPage'
import LoginPage from '@/pages/LoginPage'
import AuthCallbackPage from '@/pages/AuthCallbackPage'
import LoginHistoryPage from '@/pages/LoginHistoryPage'
import RewriteProjectsPage from '@/pages/RewriteProjectsPage'
import RewriteCreatePage from '@/pages/RewriteCreatePage'
import RewriteChannelsPage from '@/pages/RewriteChannelsPage'
import { useAuthStore } from '@/stores/authStore'

const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const GuidelinesPage = lazy(() => import('@/pages/GuidelinesPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const ContentProjectsPage = lazy(() => import('@/pages/ContentProjectsPage'))
const ImageSynthesisPage = lazy(() => import('@/pages/ImageSynthesisPage'))
const PointsPage = lazy(() => import('@/pages/PointsPage'))

const LazyFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
)

function App() {
  const { checkSession, isInitialized, isLoading } = useAuthStore()

  // 앱 시작 시 서버 세션 확인
  useEffect(() => {
    checkSession()
  }, [checkSession])

  // 세션 확인 중 로딩 표시
  if (!isInitialized && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">로딩 중...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/:provider/callback" element={<AuthCallbackPage />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="dashboard" element={<Suspense fallback={<LazyFallback />}><DashboardPage /></Suspense>} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="editor" element={<EditorPage />} />
        <Route path="editor/:id" element={<EditorPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="generate" element={<GeneratePage />} />
        <Route path="analyzer" element={<AnalyzerPage />} />
        <Route path="login-history" element={<LoginHistoryPage />} />
        <Route path="rewrite" element={<RewriteProjectsPage />} />
        <Route path="rewrite/create" element={<RewriteCreatePage />} />
        <Route path="rewrite/create/:projectId" element={<RewriteCreatePage />} />
        <Route path="rewrite/channels" element={<RewriteChannelsPage />} />
        <Route path="guidelines" element={<Suspense fallback={<LazyFallback />}><GuidelinesPage /></Suspense>} />
        <Route path="points" element={<Suspense fallback={<LazyFallback />}><PointsPage /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<LazyFallback />}><SettingsPage /></Suspense>} />
        <Route path="content-projects" element={<Suspense fallback={<LazyFallback />}><ContentProjectsPage /></Suspense>} />
        <Route path="image-synthesis" element={<Suspense fallback={<LazyFallback />}><ImageSynthesisPage /></Suspense>} />
      </Route>
    </Routes>
  )
}

export default App
